import { ApolloQueryResult, useQuery } from '@apollo/client';
import { capitalize, memoize } from 'lodash';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@atlaskit/icon/glyph/add';
import ToolTip from '@atlaskit/tooltip';
import {
  DynamicResultTable,
  FormattedDate,
  Icon,
  OpenSidebarButton,
  PaymentErrorLozenge,
  isAccess,
  isResourceAccess,
} from '@css-xp/base';
import { CommerceAddress, Maybe } from '@css-xp/graphtypes';
import {
  CreditCard,
  FetchBillingProfilesForTransactionAccountQuery,
  FetchBillingProfilesForTransactionAccountQueryVariables,
  InvoiceGroup,
  PaymentMethod,
  PaymentMethodType,
  PaymentOnTerms,
  SortDirection,
  TasstaxIdLabel,
  TaxId,
} from '@css-xp/graphtypes/src/generated-types-new';
import { INVOICE_GROUP_PATH_2, NEW_DATE_FORMAT, Permissions, Resources } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { SIDEBAR } from '../../../transaction-account';
import './billing-profiles-table.scss';
import { fetchBillingProfilesForTransactionAccount } from './queries/fetch-billing-profiles-for-transaction-account';

interface BillingProfilesTableProps {
  transactionAccountId?: string;
  onComplete?: () => Promise<ApolloQueryResult<unknown>>;
  billToAddress?: CommerceAddress | null;
  availablePaymentMethods?: Maybe<Maybe<PaymentMethod>[]> | undefined;
  updatePaymentMethodLink?: string;
  termPaymentStatus?: string | null | undefined;
}
const { INVOICE_GROUP_WRITE } = Permissions;

const defaultSearchOptions: FetchBillingProfilesForTransactionAccountQueryVariables['searchOptions'] = {
  first: DEFAULT_ROWS_PER_PAGE,
  offset: 0,
  sort: {
    sortFields: ['id'],
    sortFieldsDirections: [SortDirection.DESC],
  },
};

export const emptyStateBillingProfileProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-results-billingProfile-state',
};

const getColumns = memoize(() => {
  const columns = [
    {
      content: 'Name',
      width: 15,
    },
    {
      content: 'Sold-to address',
      width: 25,
    },
    {
      content: 'Payment method',
      width: 15,
    },
    {
      key: 'created-on',
      content: 'Created on',
      width: 10,
    },
    {
      content: 'Currency',
      width: 10,
    },
    {
      content: 'PO number',
      width: 15,
    },
    {
      content: 'Tax ID',
      width: 15,
    },
    {
      content: 'Pricing eligibility',
      width: 15,
    },
  ];

  return {
    cells: columns,
  };
});

const formatAddressLabel = (node: CommerceAddress) => {
  let formatAddressLabel1 = '';
  if (node?.name) {
    formatAddressLabel1 += node.name;
  }
  if (node?.addressLine1) {
    formatAddressLabel1 += `, ${node.addressLine1}`;
  }
  if (node?.addressLine2) {
    formatAddressLabel1 += `, ${node.addressLine2}`;
  }
  if (node?.city) {
    formatAddressLabel1 += `, ${node.city}`;
  }
  if (node?.postcode) {
    formatAddressLabel1 += `- ${node.postcode}`;
  }
  if (node?.state) {
    formatAddressLabel1 += `, ${node.state}`;
  }
  if (node?.country) {
    formatAddressLabel1 += `, ${node.country}`;
  }
  return <span className="billing-profile-address-line-clamp">{formatAddressLabel1}</span>;
};

export const getTaxContent = (invoiceGroup: InvoiceGroup | undefined) => {
  const taxIds: Maybe<Maybe<TaxId>[]> | undefined = invoiceGroup?.shipToParty?.taxIds;
  const taxLabels = invoiceGroup?.shipToParty?.taxLabels;
  let taxIdLabels: TasstaxIdLabel[] | undefined;
  if (taxLabels && taxLabels.length > 0) {
    taxIdLabels = taxLabels[0]?.taxIdLabels as TasstaxIdLabel[];
  }
  if (taxIds && taxIds.length > 0) {
    const sortedTaxDetails = taxIds
      .map((taxId: Maybe<TaxId>) => {
        const taxIdLabel = taxIdLabels?.filter(({ type }) => type && type === taxId?.label) ?? [];
        const displayTaxLabel = taxIdLabel.length > 0 ?? taxIdLabel[0].displayName;
        return { ...taxId, displayTaxLabel: displayTaxLabel };
      })
      .sort((a, b) => (a.displayTaxLabel < b.displayTaxLabel ? -1 : 1));
    return (
      <div data-testid="test-tax-ids">
        {sortedTaxDetails.map((taxId, index) => (
          <div key={taxId?.id} data-testid={`taxId-${index}`}>{`${taxId.id} (${taxId.displayTaxLabel})`}</div>
        ))}
      </div>
    );
  }
  return invoiceGroup?.shipToParty?.taxId ?? 'Not available';
};

export const formatPaymentMethod = (paymentMethod?: PaymentMethod) => {
  if (paymentMethod?.type === PaymentMethodType.CARD) {
    const creditCard = paymentMethod as CreditCard;
    const isPaymentIssue = paymentMethod?.paymentIssue === true;
    return (
      <span>
        &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {creditCard?.card?.last4Numbers}
        {isPaymentIssue && <PaymentErrorLozenge />}
      </span>
    );
  }
  if (paymentMethod?.type === PaymentMethodType.DEFERRED) {
    const paymentOnTerms = paymentMethod as PaymentOnTerms;
    return <span>Payment on terms: due in {paymentOnTerms.terms?.invoiceDueDays} days</span>;
  }
};

export const getInvoiceGroupName = (name?: string | null | undefined) => {
  if (!name || name?.toLowerCase() === 'none') {
    return `Unnamed Billing Profile`;
  }
  return name;
};

export const BillingProfilesTable = ({
  transactionAccountId,
  onComplete,
  billToAddress,
  availablePaymentMethods,
  updatePaymentMethodLink,
  termPaymentStatus,
}: BillingProfilesTableProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(DEFAULT_PAGE);
  const { loading, data, refetch } = useQuery<
    FetchBillingProfilesForTransactionAccountQuery,
    FetchBillingProfilesForTransactionAccountQueryVariables
  >(fetchBillingProfilesForTransactionAccount, {
    variables: {
      id: transactionAccountId ?? '',
      searchOptions: { first: rowsPerPage },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !transactionAccountId,
  });

  // variables to use for paging and populating our template
  const totalCount = data?.transactionAccount?.billingProfiles?.totalCount ?? 0;
  const nodes = data?.transactionAccount?.billingProfiles?.nodes ?? [];

  const showPagination = totalCount > DEFAULT_ROWS_PER_PAGE;

  const renderActionsOnTop = useCallback(() => {
    return [
      () =>
        isResourceAccess(Resources.TRANSACTION_ACCOUNT_ALL) && isAccess(INVOICE_GROUP_WRITE) ? (
          <div className="billing-profile-actions add">
            <div className="add-action">
              <OpenSidebarButton
                sidebar={SIDEBAR.CREATE_INVOICE_GROUP}
                sidebarOptions={{
                  sidebarWidth: '600px',
                  isOverlay: true,
                }}
                payload={{
                  transactionAccountId,
                  billToAddress,
                  availablePaymentMethods,
                  refetch: onComplete,
                  updatePaymentMethodLink: updatePaymentMethodLink,
                  termPaymentStatus,
                }}
                appearance="link"
                iconBefore={<AddIcon label="Add icon" size="small" />}
                testId="add-billing-profiles"
                label="Add a new billing profile"
              >
                <span>Add a new billing profile</span>
              </OpenSidebarButton>
            </div>
            <span className="add-subtext">Billing profile is sometimes referred to as invoice group.</span>
          </div>
        ) : (
          <div />
        ),
    ];
  }, [transactionAccountId, billToAddress, onComplete, updatePaymentMethodLink, availablePaymentMethods]);

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        ...defaultSearchOptions,
        offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        first: rowsPerPage,
      },
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setCurrentPage(1);
    setRowsPerPage(newRowPerPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        ...defaultSearchOptions,
        offset: 0,
        first: newRowPerPage,
      },
    });
  };

  const getRows = () => {
    return nodes.map(
      //@ts-ignore
      (invoiceGroup, index) => {
        return {
          key: invoiceGroup?.id ?? index.toString(),
          cells: [
            {
              content: (
                <>
                  {invoiceGroup?.id ? (
                    <>
                      <Link
                        to={`${INVOICE_GROUP_PATH_2}/${encodeURIComponent(invoiceGroup?.id)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="billing-profile-name"
                        data-testid="click"
                      >
                        {getInvoiceGroupName(invoiceGroup?.name)}
                      </Link>
                      {invoiceGroup?.pendingInvoices && invoiceGroup.pendingInvoices.length > 0 && (
                        <span className="billing-profile-tab-info-icon" data-testid="billing-profile-tab-info-icon">
                          <ToolTip
                            content={
                              <>
                                We’re getting some invoices ready. For more details, go the <em>Upcoming invoices </em>
                                tab of this billing profile.
                              </>
                            }
                            position="bottom-start"
                            tag="span"
                          >
                            <Icon type="info" size="small" isHovered={true} />
                          </ToolTip>
                        </span>
                      )}
                    </>
                  ) : (
                    'Not available'
                  )}
                </>
              ),
            },
            {
              content: (
                <>
                  {invoiceGroup?.shipToParty?.id
                    ? formatAddressLabel(invoiceGroup?.shipToParty)
                    : 'Same as billing address'}
                </>
              ),
            },
            {
              content: (
                <>
                  {invoiceGroup?.defaultPaymentMethod
                    ? formatPaymentMethod(invoiceGroup?.defaultPaymentMethod)
                    : 'Not available'}
                </>
              ),
            },
            {
              content: (
                <>
                  <FormattedDate date={invoiceGroup?.createdAt} format={NEW_DATE_FORMAT} />
                </>
              ),
            },
            {
              content: <>{invoiceGroup?.currency}</>,
            },
            {
              content: (
                <>
                  {invoiceGroup?.purchaseOrder?.number || '-'}
                  {invoiceGroup?.purchaseOrder?.number && (
                    <div>({invoiceGroup?.purchaseOrder?.oneTimeUse ? 'Single-use' : 'Multi-use'})</div>
                  )}
                </>
              ),
            },
            {
              content: getTaxContent(invoiceGroup as InvoiceGroup),
            },
            {
              content: (
                <>
                  {invoiceGroup?.shipToPriceEligibility
                    ? capitalize(invoiceGroup?.shipToPriceEligibility)
                    : 'Not available'}
                </>
              ),
            },
          ],
        };
      }
    );
  };

  return (
    <DynamicResultTable
      testId="billing-profiles"
      head={getColumns()}
      rows={getRows()}
      emptyStateProps={emptyStateBillingProfileProp}
      isLoading={loading}
      totalCount={totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isSearchEnabled={false}
      isPaginationEnabled={showPagination}
      onRowChange={onRowPerPageChange}
      actions={renderActionsOnTop()}
      boxShadow={false}
      className="invoice-group-table"
    />
  );
};

export default BillingProfilesTable;
